/**
 * TWA / PWA Builder APK: leftover CSSTransition classes keep
 * .page-transition-wrapper as position:fixed + overflow, which traps
 * vertical pan because html is also a scroller. Settle the entered
 * wrapper back to document flow after every route animation.
 */

const TRANSITION_PREFIXES = [
  "page",
  "slide-out-right",
  "zoom-pop",
  "zoom",
  "fade",
  "no-transition",
];

const TRANSITION_STAGES = [
  "enter",
  "enter-active",
  "enter-done",
  "exit",
  "exit-active",
  "exit-done",
  "appear",
  "appear-active",
  "appear-done",
];

const WRAPPER_STYLE_PROPS = [
  "position",
  "top",
  "left",
  "right",
  "bottom",
  "inset",
  "width",
  "height",
  "min-height",
  "max-height",
  "overflow",
  "overflow-x",
  "overflow-y",
  "transform",
  "z-index",
  "pointer-events",
  "visibility",
];

function isExitingWrapper(wrapper) {
  if (!wrapper || !wrapper.className) return false;
  return TRANSITION_PREFIXES.some((prefix) =>
    TRANSITION_STAGES.filter((stage) => stage.startsWith("exit")).some((stage) =>
      wrapper.classList.contains(`${prefix}-${stage}`)
    )
  );
}

export function releaseDocumentScrollLock() {
  if (typeof document === "undefined") return;
  const html = document.documentElement;
  const body = document.body;
  if (!html || !body) return;

  html.classList.remove("noscroll");
  body.classList.remove("noscroll");

  // Remove every inline overflow / scroll lock that Account and modal
  // helpers set. We also explicitly re-state the stylesheet values so that
  // mobile WebViews (Android Capacitor TWA / iOS PWA) that have trouble
  // re-reading the cascade after an inline removal still get the correct
  // computed style instead of staying locked.
  html.style.removeProperty("overflow");
  html.style.removeProperty("overflow-x");
  html.style.removeProperty("overflow-y");
  // Explicit restore (matches custom.css)
  html.style.overflowX = "hidden";
  html.style.overflowY = "auto";

  // Body must NOT be a second scrollport. Any non-visible overflow-x on body
  // forces overflow-y to compute to auto (CSS Overflow), which makes
  // window.scrollTo a no-op while html.scrollHeight still looks scrollable.
  body.style.removeProperty("overflow");
  body.style.removeProperty("overflow-x");
  body.style.removeProperty("overflow-y");
  body.style.setProperty("overflow-x", "visible", "important");
  body.style.setProperty("overflow-y", "visible", "important");

  html.style.removeProperty("overscroll-behavior");
  html.style.removeProperty("overscroll-behavior-y");
  body.style.removeProperty("overscroll-behavior");
  body.style.removeProperty("overscroll-behavior-y");

  // Also clear no-scroll library's position:fixed trick
  // (used by react-responsive-modal when blockScroll=true)
  if (html.style.position === "fixed") {
    const savedTop = html.style.top;
    html.style.removeProperty("position");
    html.style.removeProperty("top");
    html.style.removeProperty("width");
    // Restore scroll position that no-scroll saved via top offset
    if (savedTop) {
      const scrollY = -parseInt(savedTop, 10);
      if (Number.isFinite(scrollY) && scrollY >= 0) {
        window.scrollTo(0, scrollY);
      }
    }
  }
}

function stripTransitionStageClasses(wrapper) {
  TRANSITION_PREFIXES.forEach((prefix) => {
    TRANSITION_STAGES.forEach((stage) => {
      wrapper.classList.remove(`${prefix}-${stage}`);
    });
  });
}

function clearWrapperInlineLayout(wrapper) {
  WRAPPER_STYLE_PROPS.forEach((prop) => wrapper.style.removeProperty(prop));
}

export function settlePageTransitionWrapper(wrapper) {
  if (!wrapper) return;
  stripTransitionStageClasses(wrapper);
  clearWrapperInlineLayout(wrapper);
  wrapper.style.position = "static";
  wrapper.style.top = "auto";
  wrapper.style.left = "auto";
  wrapper.style.right = "auto";
  wrapper.style.bottom = "auto";
  wrapper.style.width = "auto";
  wrapper.style.height = "auto";
  wrapper.style.minHeight = "0";
  wrapper.style.maxHeight = "none";
  wrapper.style.overflow = "visible";
  wrapper.style.transform = "none";
  wrapper.style.zIndex = "auto";
  wrapper.style.removeProperty("pointer-events");
  wrapper.style.removeProperty("visibility");
  wrapper.removeAttribute("aria-hidden");
}

export function neutralizeStuckExitWrapper(wrapper) {
  if (!wrapper) return;
  stripTransitionStageClasses(wrapper);
  clearWrapperInlineLayout(wrapper);
  wrapper.style.setProperty("pointer-events", "none", "important");
  wrapper.style.setProperty("visibility", "hidden", "important");
  wrapper.style.setProperty("position", "absolute", "important");
  wrapper.style.setProperty("inset", "0", "important");
  wrapper.style.setProperty("width", "100%", "important");
  wrapper.style.setProperty("height", "0", "important");
  wrapper.style.setProperty("overflow", "hidden", "important");
  wrapper.style.setProperty("z-index", "-1", "important");
  wrapper.setAttribute("aria-hidden", "true");
}

function pickActiveWrapper(wrappers, enteredWrapper) {
  if (enteredWrapper && !isExitingWrapper(enteredWrapper)) {
    return enteredWrapper;
  }

  const live = wrappers.filter((node) => !isExitingWrapper(node));
  if (live.length) {
    return live[live.length - 1];
  }

  return null;
}

export function settleEnteredWrapper(enteredWrapper) {
  if (typeof document === "undefined") return;

  const wrappers = Array.from(
    document.querySelectorAll(".page-transition-wrapper")
  );
  const active = pickActiveWrapper(wrappers, enteredWrapper);

  wrappers.forEach((node) => {
    if (node === active) {
      settlePageTransitionWrapper(node);
    } else {
      neutralizeStuckExitWrapper(node);
    }
  });

  releaseDocumentScrollLock();
}
